import { CheckCircle2, Eraser } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Gated } from '@/components/permission-gate';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * SAL-01 §5's customer-acceptance card (fields A1..A6), the consuming
 * feature of the `invoicing.quote_customer_signature` company switch. The
 * pad captures pointer down/move/up/leave — a mouse, a finger and a stylus
 * all work (A1) — and **Accept quote** (A5) hands the drawing to the caller
 * as a PNG data URI so the acceptance act can STORE it with the quotation
 * (BR-SAL-01-13: "not merely drawn on screen"; the server refuses a signed
 * acceptance without it). An empty pad answers with ERR-SAL-01-07's own
 * sentence beside the pad (GEN-152: the message sits by its field):
 * "Ask the customer to sign before accepting."
 *
 * Once a signature is stored the card shows the RECORD, not a pad — what
 * was signed, read back from the document (A6's promise kept: "The
 * signature is stored with the quotation").
 */

/** A1's canvas: the prototype's own 620×150, scaled down responsively. */
const PAD_WIDTH = 620;
const PAD_HEIGHT = 150;

export function QuoteAcceptanceCard({
  stored,
  canAccept,
  busy,
  onAccept,
}: {
  /** The signature already on the quotation, if any (BR-SAL-01-13's record). */
  stored: string | null;
  /** True while the status offers acceptance (sent, or expired under R4). */
  canAccept: boolean;
  busy: boolean;
  /** Receives the pad's PNG data URI; the caller runs the acceptance act. */
  onAccept: (signature: string) => void;
}) {
  const { t } = useTranslation();
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const drawing = React.useRef(false);
  const [hasInk, setHasInk] = React.useState(false);
  const [emptyPad, setEmptyPad] = React.useState(false);

  if (stored === null && !canAccept) return null;

  const point = (event: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    // Drawing coordinates live in the canvas's own 620×150 space whatever
    // width the card lends it on screen.
    const scaleX = rect.width > 0 ? PAD_WIDTH / rect.width : 1;
    const scaleY = rect.height > 0 ? PAD_HEIGHT / rect.height : 1;
    return { x: (event.clientX - rect.left) * scaleX, y: (event.clientY - rect.top) * scaleY };
  };

  const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (busy) return;
    drawing.current = true;
    setHasInk(true);
    setEmptyPad(false);
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      const { x, y } = point(event);
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#1e293b';
      ctx.beginPath();
      ctx.moveTo(x, y);
    }
  };

  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      const { x, y } = point(event);
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };

  const stop = () => {
    drawing.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    setEmptyPad(false);
  };

  const accept = () => {
    // ERR-SAL-01-07 — "clicked Accept quote with an empty pad". The server
    // enforces the same refusal; this answers it beside the pad, before a
    // request goes anywhere.
    if (!hasInk) {
      setEmptyPad(true);
      return;
    }
    let signature = 'data:image/png;base64,';
    try {
      signature = canvasRef.current?.toDataURL('image/png') ?? signature;
    } catch {
      // jsdom has no raster backend; the pointer-tracked ink is the fact the
      // logic runs on, and a real browser always reaches toDataURL.
    }
    onAccept(signature);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('quotes.signatureTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3">
        {stored !== null ? (
          // The record: what the customer signed, stored with the quotation.
          <img
            src={stored}
            alt={t('quotes.signatureStoredAlt')}
            className="max-w-full rounded-md border border-input bg-white"
            width={PAD_WIDTH}
            height={PAD_HEIGHT}
          />
        ) : (
          <>
            <canvas
              ref={canvasRef}
              data-testid="signature-pad"
              width={PAD_WIDTH}
              height={PAD_HEIGHT}
              className="max-w-full touch-none rounded-md border border-input bg-white"
              onPointerDown={start}
              onPointerMove={move}
              onPointerUp={stop}
              onPointerLeave={stop}
            />
            {emptyPad ? (
              <p role="alert" className="text-sm font-medium text-destructive">
                {t('quotes.errSignFirst')}
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" onClick={clear} disabled={busy}>
                <Eraser aria-hidden /> {t('quotes.clearSignature')}
              </Button>
              <Gated permission="sales.edit" reasonKey="perm.cannot.moveQuotes">
                <Button type="button" onClick={accept} disabled={busy}>
                  <CheckCircle2 aria-hidden /> {t('quotes.acceptQuote')}
                </Button>
              </Gated>
            </div>
            {/* A6 — printed under the buttons, word for word. */}
            <p className="text-xs text-muted-foreground">{t('quotes.signatureHint')}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
